import React from "react";
import Button from "components/button/Button";
import Select from "react-select";
import BannerCard from "components/card/BannerCard";
import { useNavigate } from "react-router-dom";
import { TiPlus } from "react-icons/ti";
import { toast } from "react-hot-toast";
import { Spinner } from "@chakra-ui/react";
import { debounce } from "lodash";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { IBanners } from "./types/banners.interface";
import { PiDotsThreeBold } from "react-icons/pi";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { FiSearch } from "react-icons/fi";
import { deleteBanner, getBanners } from "api/BannerAPI";

const totalItemsOptions: {
  value: number;
}[] = [
  { value: 10 },
  { value: 20 },
  { value: 25 },
  { value: 50 },
  { value: 100 },
];

const Banner = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [banners, setBanners] = React.useState<IBanners[]>([]);
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    limit: NumberParam,
    search: StringParam,
  });
  const [searchKey, setSearchKey] = React.useState<string>(null);
  const [limit, setLimit] = React.useState<number>(10);
  const [nextPage, setNextPage] = React.useState<number>(null);
  const [prevPage, setPrevPage] = React.useState<number>(null);
  const [lastPage, setLastPage] = React.useState<number>(null);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageState, setPageState] = React.useState({
    startIdx: 0,
    endIdx: 0,
  });
  const [pageNumbers, setPageNumbers] = React.useState<number[]>([]);

  const getBannersWithParams = (
    page?: number,
    limit?: number,
    keyword?: string
  ): void => {
    setIsLoading(true);
    const promise = getBanners(page, limit, keyword);

    promise
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          setIsLoading(false);
          setBanners(res.data.data.data);
          setNextPage(res.data.data?.meta?.next_page);
          setPrevPage(res.data.data?.meta?.prev_page);
          setLastPage(res.data.data?.meta?.last_page);
          setCurrentPage(res.data.data?.meta?.current_page);
          setPageNumbers(
            res.data.data?.meta?.last_page &&
              Array.from(
                Array(res.data.data?.meta?.last_page + 1)?.keys()
              )?.slice(1)
          );
          if (res.data.data?.meta?.current_page === pageState.endIdx + 1) {
            setPageState({
              startIdx: pageState.endIdx,
              endIdx: pageState.endIdx + 5,
            });
          } else if (res.data.data?.meta?.current_page === pageState.startIdx) {
            setPageState({
              startIdx: pageState.startIdx - 5,
              endIdx: pageState.endIdx - 5,
            });
          }
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(err?.response?.data?.message || "Gagal memuat banner!");
      });
  };

  const jumpForwardPage = () => {
    setPageState({
      startIdx: pageState.endIdx,
      endIdx: pageState.endIdx + 5,
    });
  };

  const jumpBackwardPage = () => {
    setPageState({
      startIdx: pageState.startIdx - 5,
      endIdx: pageState.endIdx - 5,
    });
  };

  const handleNextPage = () => {
    if (currentPage === lastPage) return;
    setCurrentPage(nextPage);
    getBannersWithParams(nextPage, limit, searchKey);
  };

  const handlePrevPage = () => {
    if (currentPage === 1) return;
    setCurrentPage(prevPage);
    getBannersWithParams(prevPage, limit, searchKey);
  };

  const handleSearchKey = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    setSearchKey(e.target.value === "" ? null : e.target.value);
    getBannersWithParams(currentPage, limit, e.target.value);
    setQuery({
      page: currentPage,
      search: e.target.value === "" ? null : e.target.value,
    });
  };

  const debounceRes = React.useMemo(() => {
    return debounce(handleSearchKey, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = (slug: string): void => {
    const promise = deleteBanner(slug);

    toast.promise(promise, {
      loading: "Menghapus banner...",
      success: (res) => {
        if (res.status === 200) {
          getBannersWithParams(query.page, query.limit, query.search);
        }

        return res.data.message || "Berhasil menghapus banner!";
      },
      error: "Gagal menghapus banner!",
    });
  };

  React.useEffect(() => {
    getBannersWithParams(query.page, query.limit, query.search);

    return () => {
      debounceRes.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-3 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-fit w-full">
        {/* Recenlty Added setion */}
        <div className="mb-5 mt-5 flex items-center justify-between px-[26px]">
          <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
            Banner
          </h4>
          <div className="flex gap-3">
            <Button
              size="fit"
              variant="primary"
              children={
                <div className="flex items-center gap-1">
                  <TiPlus /> Banner
                </div>
              }
              onClick={() => {
                navigate("/admin/banners/add");
              }}
            />
          </div>
        </div>
        <div className="mb-5 flex w-full items-center justify-between gap-8 px-[26px]">
          <div className="col-span-8 flex h-12 w-full flex-3 items-center rounded-full border-2 border-gray-300 bg-gray-100 text-navy-700 dark:bg-navy-900 dark:text-white">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              type="text"
              placeholder="Coba cari nama banner..."
              onChange={(e) => debounceRes(e)}
              defaultValue={searchKey}
              className="block h-full w-full rounded-full bg-gray-100 text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white"
            />
          </div>
          <Select
            options={totalItemsOptions}
            placeholder="Total Item"
            classNames={{
              placeholder: () => `!text-brand-500 dark:text-white/50`,
              control: () =>
                `flex !h-12 w-full items-center justify-center !rounded-3xl font-semibold !bg-white !pb-3 !pl-3 !pt-1 text-sm !outline-none transition-all duration-300 !border-none dark:!border-white/10 dark:text-white !cursor-pointer`,
              container: () => `w-32`,
              dropdownIndicator: () => `!text-brand-500`,
              indicatorSeparator: () => `!hidden`,
              option: () => `!cursor-pointer`,
            }}
            getOptionLabel={(option) => `${option.value} item`}
            onChange={(val) => {
              setLimit(val?.value || 10);
              getBannersWithParams(currentPage, val?.value || 10, searchKey);
              setQuery({
                page: currentPage,
                limit: val?.value || 10,
                search: searchKey,
              });
            }}
          />
        </div>
        {!isLoading ? (
          banners?.length > 0 ? (
            <div className="z-20 grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3">
              {banners.map((banner, idx) => (
                <div key={idx}>
                  <BannerCard
                    id={banner.id}
                    name={banner.name}
                    link={banner.link}
                    image={banner.image}
                    slug={banner.slug}
                    key={idx}
                    is_active={banner.is_active}
                    is_main={banner.is_main}
                    onDelete={() => handleDelete(banner.slug)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-80 items-center justify-center">
              <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
                {!searchKey
                  ? "Belum ada banner yang ditambahkan"
                  : "Banner tidak ditemukan"}
              </p>
            </div>
          )
        ) : (
          <div className="flex h-80 w-full flex-col items-center justify-center">
            <Spinner size="xl" className="text-brand-400" />
          </div>
        )}
        {!isLoading && banners?.length > 0 && (
          <div className="pagination my-5 flex w-full justify-center">
            <div className="pagination__item flex items-center gap-2">
              <button
                className="pagination__button"
                onClick={() => handlePrevPage()}
              >
                <BiChevronLeft className="text-xl" />
              </button>
              <div className="flex items-center justify-center gap-1">
                {pageState.startIdx >= 1 && (
                  <button
                    className="pagination__button"
                    onClick={() => jumpBackwardPage()}
                  >
                    <PiDotsThreeBold className="text-lg" />
                  </button>
                )}
                {pageNumbers
                  .slice(pageState.startIdx, pageState.endIdx)
                  .map((page, idx) => (
                    <button
                      className={`pagination__button ${
                        currentPage === page ? "pagination__button--active" : ""
                      }`}
                      onClick={() => {
                        setCurrentPage(page);
                        getBannersWithParams(page, limit, searchKey);
                        setQuery({
                          page: page,
                          limit: limit,
                          search: searchKey,
                        });
                      }}
                      key={idx}
                    >
                      {page}
                    </button>
                  ))}
                {pageState.endIdx <= lastPage && (
                  <button
                    className="pagination__button"
                    onClick={() => jumpForwardPage()}
                  >
                    <PiDotsThreeBold className="text-lg" />
                  </button>
                )}
              </div>
              <button
                className="pagination__button"
                onClick={() => handleNextPage()}
              >
                <BiChevronRight className="text-xl" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Banner;
